import React, { useState, useCallback } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';
import { parseString } from 'xml2js';
import { axios } from '@playbookux/playbook-web-core';
import client from '../../services/client';
import UploadCompletedComponent from './upload-completed-component';

import {
  Label,
  Input,
  Icon,
  ProgressBar,
  ContainerProgressbar,
  Button,
  VideoTextContainer,
  Text,
} from './styles';

const MEGABYTE = 1024 * 1024;
const MESSAGE_KEYS = {
  'image/jpeg, image/png': 'png and jpg files only',
  'video/mp4': 'mp4 files only',
  'application/pdf': 'pdf files only',
};
const SIZE_KEYS = {
  'image/jpeg, image/png': '10MB',
  'video/mp4': '500MB',
};

const schema = Yup
  .mixed()
  .test(
    'size',
    '',
    function checkFileSize(value) {
      const { limitSize } = this.options.context;

      if (value === null || (value && value.size >= limitSize)) {
        return this.createError({
          path: 'size',
          message: `File size exceeds ${limitSize / MEGABYTE}MB`,
        });
      }
      return true;
    },
  )
  .test(
    'type',
    '',
    function checkFileType(value) {
      const { supportedFormats } = this.options.context;

      if (value === null || (value && !supportedFormats.includes(value.type))) {
        return this.createError({
          path: 'type',
          message: `${MESSAGE_KEYS[supportedFormats.join(', ')]}`,
        });
      }
      return true;
    },
  );

// this component does have a lot of different responsabilities.
// we may need to divide them and rework the implementations of it.
const UploadFileButton = (props) => {
  const {
    text,
    index,
    onSetUploadError,
    endpointUrl,
    onChangeMedia,
    mediaUrl,
    mediaKey,
    maxFileSize,
    supportedFormats,
    maxHeight,
    canEditMedia,
    mediaType,
    uploadCompletedFc: UploadCompletedFc = UploadCompletedComponent,
    setIsUploadDone, // use useState to prevent user clicking any button while it is uploading
  } = props;

  const [progressStatus, setProgressStatus] = useState(null);
  const [hasError, setHasError] = useState(false);

  const uploadMedia = useCallback((progress) => {
    setProgressStatus(progress);
    if (setIsUploadDone && progress === 100) {
      setIsUploadDone(true);
    }
  }, []);

  const handleResetUpload = () => {
    if (canEditMedia) {
      onChangeMedia(null, null, index);
      setProgressStatus(null);
      onSetUploadError('');
      setHasError(false);
    }
  };

  const requestUpload = async (url, filename, cloudFrontUrl, formData, event, handleUploadProgress = () => {}) => {
    try {
      await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: handleUploadProgress,
      });

      onChangeMedia(cloudFrontUrl, filename, index);
    } catch (error) {
      setHasError(true);
      onSetUploadError('Error uploading. Please try again');
      onChangeMedia(null, null, index);

      const { response } = error;

      if (error && error.response) {
        // convert XML error message to JS object
        parseString(response.data, (err, result) => {
          if (result.Error.Code[0] === 'EntityTooLarge') {
            onSetUploadError(`File size exceeds ${SIZE_KEYS[supportedFormats.join(', ')]}`);
          }
        });
      }
    }
  };

  // get url to upload direclty to s3
  const getPressignedUrl = async (filename, contentType = null) => {
    try {
      const options = {
        filename,
        content_type: contentType,
      };
      const { data } = await client.post(endpointUrl, {
        media: options,
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      return data;
    } catch (e) {
      setHasError(true);
      onSetUploadError('Error uploading. Please try again');
      return null;
    }
  };

  const handleuploadMedia = async (event) => {
    const newEvent = { ...event };
    const [uploadedMedia] = newEvent.target.files;

    if (setIsUploadDone) {
      setIsUploadDone(false);
    }

    if (uploadedMedia && canEditMedia) {
      try {
        await schema.validate(uploadedMedia, {
          context: {
            limitSize: maxFileSize,
            supportedFormats,
          },
        });
      } catch (err) {
        setHasError(true);
        onSetUploadError(err.message);
        return;
      }

      /** Extract extension and content-type of original filename */
      const extension = uploadedMedia.name.split('.').pop();
      const contentType = uploadedMedia.type;
      const filename = `${v4()}.${extension}`;

      const data = await getPressignedUrl(filename, contentType);

      if (!data) {
        return;
      }

      const { url, fields, cloudFrontUrl } = data;

      // creating object to send to s3
      const formData = new FormData();
      Object.keys(fields).forEach((key) => {
        formData.set(key, fields[key]);
      });
      formData.append('file', newEvent.target.files[0], filename);

      // Upload media to s3 and get progress
      requestUpload(url, filename, cloudFrontUrl, formData, newEvent, (e) => {
        const progress = Math.round((e.loaded * 100) / e.total);
        uploadMedia(progress);
        return progress;
      });
    }
  };

  const resetValue = (event) => {
    const newEvent = { ...event };
    handleResetUpload();
    newEvent.target.value = null;
  };

  if (mediaUrl) {
    if (mediaType === 'video') {
      const filename = mediaKey.includes('.mp4') ? mediaKey : `${mediaKey}.mp4`;

      return (
        <VideoTextContainer>
          <Text>
            {`Your video is uploaded: ${filename}`}
          </Text>
          <Button
            onClick={handleResetUpload}
          >
            Delete
          </Button>
        </VideoTextContainer>
      );
    }
    return (
      <UploadCompletedFc
        imageUrl={mediaUrl}
        imageKey={mediaKey}
        onRemove={handleResetUpload}
        maxHeight={maxHeight}
        canRemoveImage={canEditMedia}
        // when this component shows up, it means the upload is completed
        setIsUploadDone={setIsUploadDone}
      />
    );
  }

  if (progressStatus) {
    return (
      <ContainerProgressbar>
        <ProgressBar
          percent={progressStatus}
          error={hasError}
          size="small"
          key={index}
        />
        {hasError && (
          <Icon
            name="x"
            onClick={handleResetUpload}
          />
        )}
      </ContainerProgressbar>
    );
  }

  return (
    <>
      <Input
        onChange={handleuploadMedia}
        onClick={resetValue}
        type="file"
        id={`upload#${index}`}
        hidden
        accept={supportedFormats.join(', ')}
      />
      <Label
        htmlFor={`upload#${index}`}
      >
        <Icon name="upload" />
        {text}
      </Label>
    </>
  );
};

UploadFileButton.propTypes = {
  text: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onSetUploadError: PropTypes.func.isRequired,
  endpointUrl: PropTypes.string.isRequired,
  onChangeMedia: PropTypes.func.isRequired,
  values: PropTypes.shape({
    cardSortItems: PropTypes.shape({
      mediaUrl: PropTypes.string,
    }),
  }).isRequired,
  mediaUrl: PropTypes.string.isRequired,
  mediaKey: PropTypes.string.isRequired,
  maxFileSize: PropTypes.number.isRequired,
  supportedFormats: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxHeight: PropTypes.number,
  canEditMedia: PropTypes.bool,
  mediaType: PropTypes.string.isRequired,
  uploadCompletedFc: PropTypes.func,
  setIsUploadDone: PropTypes.func,
};

UploadFileButton.defaultProps = {
  maxHeight: undefined,
  canEditMedia: true,
  uploadCompletedFc: UploadCompletedComponent,
  setIsUploadDone: undefined,
};

export default UploadFileButton;
